import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const OPTIONS = [
  { value: 'light', label: 'Light', description: 'Bright and clear', icon: Sun },
  { value: 'dark', label: 'Dark', description: 'Easy on the eyes', icon: Moon },
  { value: 'auto', label: 'Auto', description: 'Match device', icon: Monitor },
] as const;

interface ThemeModeToggleProps {
  compact?: boolean;
}

export function ThemeModeToggle({ compact = false }: ThemeModeToggleProps) {
  const { themeMode, setThemeMode } = useTheme();

  if (compact) {
    return (
      <div className="grid grid-cols-3 gap-2">
        {OPTIONS.map((option) => {
          const Icon = option.icon;
          const selected = themeMode === option.value;

          return (
            <button
              key={option.value}
              onClick={() => setThemeMode(option.value)}
              className={`flex flex-col items-center gap-1.5 rounded-lg border-2 p-3 transition-all ${
                selected
                  ? 'bg-blue-50 border-blue-500 text-blue-600'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
              type="button"
            >
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {OPTIONS.map((option) => {
        const Icon = option.icon;
        const selected = themeMode === option.value;
        const iconClassName =
          option.value === 'light'
            ? 'from-blue-400 to-blue-600'
            : option.value === 'dark'
              ? 'from-gray-700 to-gray-900'
              : 'from-purple-500 to-pink-500';

        return (
          <button
            key={option.value}
            onClick={() => setThemeMode(option.value)}
            className={`relative rounded-2xl border-2 p-6 transition-all ${
              selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            type="button"
          >
            {selected && (
              <div className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600">
                <span className="text-sm font-bold text-white">✓</span>
              </div>
            )}
            <div className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${iconClassName}`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <p className="mb-1 font-bold text-gray-900">{option.label}</p>
            <p className="text-sm text-gray-600">{option.description}</p>
          </button>
        );
      })}
    </div>
  );
}
